import React from "react";
import { css } from "@linaria/core";
import { color, fonts } from "@ui";
import { spacings } from "@ui/Theme";
import { NONE_LABEL } from "@ui/Filter";
import t from "../../i18n";
import SelectableQuantitiesGroup from "./QuantitySelector/SelectableQuantitiesGroup";
import { getQuantitySelectorLists } from "./QuantitySelector/getQuantitySelectorLists";
import { getQuantityTypeLabel } from "./Settings/Quantities/utils/getQuantityType";
import type { Quantity } from "../../api/useQuantities";

type Props = Omit<
  React.ComponentProps<typeof SelectableQuantitiesGroup>,
  "quantities" | "getQuantityGroupLabel"
> & {
  visibleQuantities: Quantity[];
  groupingType: "SYSTEM" | "UNIT" | "NATURAL" | null;
};

function SelectedQuantitiesSection({ visibleQuantities, groupingType, ...groupProps }: Props) {
  const { selectedQuantities } = groupProps;
  const quantities = React.useMemo(
    () => getQuantitySelectorLists(visibleQuantities, [], selectedQuantities).selected,
    [visibleQuantities, selectedQuantities],
  );
  const getQuantityGroupLabel = React.useCallback(
    (quantity: Quantity) => {
      switch (groupingType) {
        case "SYSTEM":
          return quantity.systemId ?? t("measuresSymptoms.collapse.noGroup");
        case "UNIT":
          return quantity.unit ?? NONE_LABEL;
        case "NATURAL":
          return getQuantityTypeLabel(quantity.type);
        case null:
          return "";
        default:
          throw new Error(groupingType satisfies never);
      }
    },
    [groupingType],
  );

  if (quantities.length === 0) return null;

  return (
    <section className={selectedQuantitiesSection} data-cy="selected-quantities">
      <h3 className={selectedQuantitiesTitle}>{t("quantitySelector.selected.title")}</h3>
      <SelectableQuantitiesGroup
        {...groupProps}
        quantities={quantities}
        getQuantityGroupLabel={getQuantityGroupLabel}
      />
    </section>
  );
}

export default React.memo(SelectedQuantitiesSection);

const selectedQuantitiesSection = css`
  div:has(> &) {
    padding-top: 0;
  }

  padding-top: 16px;
  background-color: ${color.titanium50};
  flex-shrink: 0;
  width: 100%;
  padding-bottom: ${spacings.m};
  border-bottom: 1px solid ${color.titanium200};
`;

const selectedQuantitiesTitle = css`
  margin: 0 0 ${spacings.s};
  color: ${color.carbon900};
  font: ${fonts.body1SemiBold};
`;
